import { registerLocaleData } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import ptBr from '@angular/common/locales/pt';
import { APP_INITIALIZER, ErrorHandler, LOCALE_ID, NgModule } from '@angular/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { BrowserModule } from '@angular/platform-browser';
import {
  BrowserAnimationsModule,
  provideAnimations,
} from '@angular/platform-browser/animations';
import { FullCalendarModule } from '@fullcalendar/angular';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslateModule } from '@ngx-translate/core';
import { InlineSVGModule } from 'ng-inline-svg-2';
import { defineLocale } from 'ngx-bootstrap/chronos';
import {
  BsDatepickerConfig,
  BsDatepickerModule,
  BsDaterangepickerConfig,
} from 'ngx-bootstrap/datepicker';
import { ptBrLocale } from 'ngx-bootstrap/locale';
import { ClipboardModule } from 'ngx-clipboard';
import { provideEnvironmentNgxLoaderIndicator } from 'ngx-loader-indicator';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { NgxPermissionsModule } from 'ngx-permissions';
import { Router } from '@angular/router';
import * as Sentry from "@sentry/angular";

import { SplashScreenModule } from './_metronic/partials/layout/splash-screen/splash-screen.module';
import { AppCompanyResolverGuard } from './app-company-resolver.guard';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HttpUtilsService, LayoutUtilsService } from './core/_base/crud';
import { AuthAdminService } from './core/auth/service/auth-admin.service';
import { AuthModule } from './modules/auth/auth.module';
import { CanDeactivateGuard } from './shared/crud/can-deactivate.guard';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule } from '@angular/material/select';

export function CustomPaginator() {
  const customPaginatorIntl = new MatPaginatorIntl();

  customPaginatorIntl.itemsPerPageLabel = 'Itens por página:';
  customPaginatorIntl.nextPageLabel = 'Próxima página';
  customPaginatorIntl.previousPageLabel = 'Página anterior';
  customPaginatorIntl.lastPageLabel = 'Última página';
  customPaginatorIntl.firstPageLabel = 'Primeira página';
  customPaginatorIntl.getRangeLabel = getRangeLabel;

  return customPaginatorIntl;
}

export function datepickerConfig(): BsDatepickerConfig {
  const config = new BsDatepickerConfig();
  config.showWeekNumbers = false;

  return config;
}

export function daterangepickerConfig(): BsDaterangepickerConfig {
  const config = new BsDaterangepickerConfig();
  config.showWeekNumbers = false;

  return config;
}

const getRangeLabel = (page: number, pageSize: number, length: number) => {
  if (length === 0 || pageSize === 0) {
    return `0 de ${length}`;
  }
  length = Math.max(length, 0);
  const startIndex = page * pageSize;
  const endIndex =
    startIndex < length
      ? Math.min(startIndex + pageSize, length)
      : startIndex + pageSize;
  return `${startIndex + 1} - ${endIndex} de ${length}`;
};

registerLocaleData(ptBr);

defineLocale('pt-br', ptBrLocale);

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    TranslateModule.forRoot(),
    HttpClientModule,
    NgxPermissionsModule.forRoot(),
    ClipboardModule,
    AppRoutingModule,
    InlineSVGModule.forRoot(),
    AuthModule.forRoot(),
    BsDatepickerModule.forRoot(),
    SplashScreenModule,
    NgSelectModule,
    FullCalendarModule,
    MatTooltipModule,
    MatSelectModule
  ],
  providers: [
    AuthAdminService,
    provideEnvironmentNgxMask(),
    provideAnimations(),
    provideEnvironmentNgxLoaderIndicator(),
    HttpUtilsService,
    LayoutUtilsService,
    CanDeactivateGuard,
    {
      provide: MatPaginatorIntl,
      useValue: CustomPaginator(),
    },
    { provide: LOCALE_ID, useValue: 'pt-BR' },
    { provide: BsDatepickerConfig, useFactory: datepickerConfig },
    { provide: BsDaterangepickerConfig, useFactory: daterangepickerConfig },
    AppCompanyResolverGuard,
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
